export const meta = {
  title: 'Cently can pick up where you left off',
  date: '2026-07-29',
  description:
    'Cently can now search your past conversations for goals, decisions, and preferences you already discussed, then check your current numbers before it answers.',
  metaDescription:
    "Ask Cently about a goal, decision, or preference from an earlier chat. It can find past conversations by words or meaning, then check today's financial data.",
  keywords: [
    'financial assistant memory',
    'AI financial assistant',
    'conversation history',
    'personal finance app',
    'Cently chat',
    'saved prompts',
  ],
};

_July 29, 2026_

Last month you told Cently you were saving for a house. You talked through a target, checked what you could set aside, and moved on.

Today you open a new chat and ask, "How am I doing on that?"

That used to stall. Cently could read your accounts, but it could not find the earlier conversation that made "that" mean anything. Now it can.

## What Cently can look up

The useful stuff in a money conversation is rarely a single transaction. It is the plan you made, the preference you stated, or the decision you already worked through.

Cently can search your past chats for things like:

- goals you set ("we're saving for a down payment")
- decisions you made together ("we decided the car insurance was fine for now")
- preferences you said out loud ("please be brief with your response")
- the thread where you compared two options and picked one

You do not have to keep one chat open forever or paste old answers into a new one. Earlier conversations, including ones from before this update, are available when the question needs them. This works in regular chat and in saved prompts.

## Find the idea, not only the exact words

You might ask about your "house goal" when the earlier chat said "saving for a down payment." Or ask about "that Netflix thing" when the conversation never used that phrasing again.

Cently searches both ways: by the words you used, and by related meaning. That makes it more likely to find the right discussion without a scavenger hunt through your history.

![Cently finds an earlier down payment chat and answers with current savings](/blog/images/cently-assistant-memory.png)

## Current numbers still decide the answer

An old conversation is context. It is not a frozen picture of your money.

When you ask about progress, Cently uses the earlier chat to understand the goal, then checks your current balances and transactions for the answer. If things changed since you last talked, today's data wins.

That is the balance we wanted: enough memory to pick up the thread, not so much that stale details override what is true now.

## Try a question you have already asked

Open Cently and ask about a goal, preference, or decision from an earlier conversation. Start with the topic you remember. Cently can find the rest.

[Ask Cently something you discussed before →](https://cently.ai)
